import { IBookmarkRepository, BookmarkEntity, ResponseValueObject } from '@gishub/domain';
import { IBookmarkDatasource } from '../datasources/i-bookmark-datasource';
import { LocalStorageBookmarkDatasource } from '../datasources/local-storage-bookmark-datasource';
import { ApiBookmarkDatasource } from '../datasources/api-bookmark-datasource';

export class BookmarkRepository implements IBookmarkRepository {
  private readonly localDatasource: IBookmarkDatasource;
  private readonly apiDatasource: IBookmarkDatasource;

  private navigatorOffline: boolean;
  private serverUnreachable = false;
  private syncing = false;

  onSynced?: () => void;

  constructor(
    localDatasource: IBookmarkDatasource = new LocalStorageBookmarkDatasource(),
    apiDatasource: IBookmarkDatasource = new ApiBookmarkDatasource(),
  ) {
    this.localDatasource = localDatasource;
    this.apiDatasource = apiDatasource;
    this.navigatorOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false;
    this.listenConnectivity();
    if (!this.navigatorOffline) this.flushPending().catch(() => {});
  }

  private listenConnectivity(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('online', () => {
      this.navigatorOffline = false;
      this.flushPending().catch(() => {});
    });
    window.addEventListener('offline', () => {
      this.navigatorOffline = true;
    });
  }

  private isNetworkError(e: any): boolean {
    return !e || typeof e.status !== 'number';
  }

  private async run<T>(
    apiCall: () => Promise<ResponseValueObject<T>>,
    localCall: () => Promise<ResponseValueObject<T>>,
  ): Promise<ResponseValueObject<T>> {
    if (this.navigatorOffline) return localCall();
    try {
      const result = await apiCall();
      if (this.serverUnreachable) {
        this.serverUnreachable = false;
        this.flushPending().catch(() => {});
      }
      return result;
    } catch (e) {
      if (this.isNetworkError(e)) {
        this.serverUnreachable = true;
        return localCall();
      }
      throw e;
    }
  }

  private async flushPending(): Promise<void> {
    if (this.syncing) return;
    const localResult = await this.localDatasource.getAll('');
    if (!localResult.success || !localResult.data || localResult.data.length === 0) return;
    this.syncing = true;
    let syncedAny = false;
    try {
      for (const bookmark of localResult.data) {
        const created = await this.apiDatasource.create(bookmark);
        if (!created.success) break;
        syncedAny = true;
        await this.localDatasource.delete(bookmark);
      }
    } finally {
      this.syncing = false;
    }
    if (syncedAny) this.onSynced?.();
  }

  async getAll(username: string): Promise<ResponseValueObject<BookmarkEntity[]>> {
    return this.run(() => this.apiDatasource.getAll(username), () => this.localDatasource.getAll(username));
  }

  async getById(id: string | number): Promise<ResponseValueObject<BookmarkEntity>> {
    return this.run(() => this.apiDatasource.getById(id), () => this.localDatasource.getById(id));
  }

  async create(entity: BookmarkEntity): Promise<ResponseValueObject<BookmarkEntity>> {
    return this.run(() => this.apiDatasource.create(entity), () => this.localDatasource.create(entity));
  }

  async update(entity: BookmarkEntity): Promise<ResponseValueObject<BookmarkEntity>> {
    if (entity.id == null) {
      const result = await this.localDatasource.update(entity);
      if (result.success && !this.navigatorOffline) this.flushPending().catch(() => {});
      return result;
    }
    return this.run(() => this.apiDatasource.update(entity), () => this.localDatasource.update(entity));
  }

  async delete(entity: BookmarkEntity): Promise<ResponseValueObject<boolean>> {
    return this.run(() => this.apiDatasource.delete(entity), () => this.localDatasource.delete(entity));
  }
}
